import {
  ModDivider,
  ModIconButton,
  ModInputField,
  ModSkeleton
} from '@moderneinc/design-system-components';
import { iconSize, padding, spacing } from '@moderneinc/design-system-tokens';
import { Box, Stack } from '@mui/material';
import { usePopupState } from 'material-ui-popup-state/hooks';
import { useRouter } from 'next/router';
import React, { type FunctionComponent, useContext, useMemo } from 'react';
import {
  CHANGELOG_PATH,
  CODE_SEARCH_PATH,
  DEPLOYMENT_PATH,
  DEVCENTER_PATH,
  MARKETPLACE_HOME_PATH,
  MODDY_PATH,
  NEW_BUILDER_PATH,
  ORGANIZATION_REPOSITORIES_BASE_PATH,
  RECENT_ACTIVITY_PATH
} from '../../../constants/general';
import { ConfigurationContext } from '../../../contexts/config.context';
import {
  activityUrl,
  changelogUrl,
  codeSearchUrl,
  devCenterUrl
} from '../../../helpers/link.helper';
import { globalSearchEvent } from '../../../hooks/use-app-event.hooks';
import { useCapabilities } from '../../../hooks/use-capabilities.hooks';
import { useHelpMenuCustomizations } from '../../../hooks/use-help-menu-customizations.hooks';
import { useLlmConfiguration } from '../../../hooks/use-llm-configuration.hooks';
import {
  AiIcon,
  BrowseRecipesIcon,
  BuilderIcon,
  ChangelogIcon,
  CloseIcon,
  CodeSearchIcon,
  DeployRecipesIcon,
  DevCenterIcon,
  RecentActivityIcon,
  RepositoryIcon,
  SearchIcon
} from '../../../icons/icons';
import { useSelectedOrganizationStore } from '../../../stores/organization-store';
import { ModerneCliDialog } from '../../account-menu/moderne-cli/moderne-cli-dialog.component';
import {
  FALLBACK_HELP_LINKS,
  type HelpMenuLink,
  RESOURCE_LINKS,
  TOOL_LINKS
} from '../../navigation/help-menu/help-menu.data';
import { OverflowMenuAccountFooter } from './overflow-menu-account-footer.component';
import { OverflowMenuRow } from './overflow-menu-row.component';
import { OverflowMenuSection } from './overflow-menu-section.component';

type OverflowMenuContentProps = {
  onClose: () => void;
};

const isRouteSelected = (asPath: string, href: string): boolean =>
  new RegExp(`^${href}`, 'i').test(asPath);

type HelpLinkRowProps = {
  link: HelpMenuLink;
  onClose: () => void;
};

const HelpLinkRow: FunctionComponent<HelpLinkRowProps> = ({
  link,
  onClose
}) => {
  const Icon = link.icon;
  const externalProps = link.external
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};
  return (
    <OverflowMenuRow
      icon={Icon ? <Icon /> : undefined}
      label={link.label}
      href={link.getHref()}
      onClick={onClose}
      indent={!Icon}
      {...externalProps}
    />
  );
};

type ModerneCliRowProps = {
  cliLinkOverride?: { label: string; uri: string };
  onClose: () => void;
};

const ModerneCliRow: FunctionComponent<ModerneCliRowProps> = ({
  cliLinkOverride,
  onClose
}) => {
  const popupState = usePopupState({
    variant: 'dialog',
    popupId: 'overflow-menu-moderne-cli-dialog'
  });
  const { config } = useContext(ConfigurationContext);
  const cliBaseUrl = `https://${config.name}.moderne.io`;

  if (cliLinkOverride) {
    return (
      <OverflowMenuRow
        label={cliLinkOverride.label}
        onClick={() => {
          onClose();
          window.open(cliLinkOverride.uri, '_blank');
        }}
        indent
      />
    );
  }

  return (
    <React.Fragment>
      <OverflowMenuRow
        label="Moderne CLI"
        onClick={() => popupState.open()}
        indent
        data-testid="overflow-menu-moderne-cli"
      />
      <ModerneCliDialog popupState={popupState} cliBaseUrl={cliBaseUrl} />
    </React.Fragment>
  );
};

export const OverflowMenuContent: FunctionComponent<
  OverflowMenuContentProps
> = ({ onClose }) => {
  const { asPath } = useRouter();
  const { changelog, codeSearch } = useCapabilities();
  const { showModdy, isLoadingConfiguration } = useLlmConfiguration();
  const { selectedOrganizationId } = useSelectedOrganizationStore();
  const {
    moreHelp,
    cliDownload,
    loading: helpLoading
  } = useHelpMenuCustomizations();

  const devCenterHref = selectedOrganizationId
    ? devCenterUrl({ organizationId: selectedOrganizationId })
    : DEVCENTER_PATH;

  const changelogHref = selectedOrganizationId
    ? changelogUrl(selectedOrganizationId)
    : CHANGELOG_PATH;

  const codeSearchHref = selectedOrganizationId
    ? codeSearchUrl(selectedOrganizationId)
    : CODE_SEARCH_PATH;

  const activityHref = selectedOrganizationId
    ? activityUrl(selectedOrganizationId)
    : RECENT_ACTIVITY_PATH;

  const handleSearchClick = () => {
    onClose();
    globalSearchEvent.dispatch();
  };

  const navItems = useMemo(
    () => [
      ...(codeSearch
        ? [
            {
              href: codeSearchHref,
              icon: <CodeSearchIcon />,
              label: 'Code search',
              beta: true,
              tag: 'Preview'
            }
          ]
        : []),
      ...(!isLoadingConfiguration && showModdy
        ? [
            {
              href: MODDY_PATH,
              icon: <AiIcon />,
              label: 'Moddy',
              beta: true
            }
          ]
        : []),
      {
        href: devCenterHref,
        icon: <DevCenterIcon />,
        label: 'DevCenter'
      },
      {
        href: MARKETPLACE_HOME_PATH,
        icon: <BrowseRecipesIcon />,
        label: 'Marketplace'
      },
      {
        href: NEW_BUILDER_PATH,
        icon: <BuilderIcon />,
        label: 'Builder'
      },
      {
        href: activityHref,
        icon: <RecentActivityIcon />,
        label: 'Activity'
      },
      ...(changelog
        ? [
            {
              href: changelogHref,
              icon: <ChangelogIcon />,
              label: 'Changelog',
              beta: true
            }
          ]
        : []),
      {
        href: ORGANIZATION_REPOSITORIES_BASE_PATH,
        icon: <RepositoryIcon />,
        label: 'Repositories'
      },
      {
        href: DEPLOYMENT_PATH,
        icon: <DeployRecipesIcon />,
        label: 'Deploy'
      }
    ],
    [
      activityHref,
      changelog,
      changelogHref,
      codeSearch,
      codeSearchHref,
      devCenterHref,
      isLoadingConfiguration,
      showModdy
    ]
  );

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          gap: `${spacing.spacing_1}px`,
          paddingTop: `${spacing.spacing_2}px`,
          paddingBottom: `${spacing.spacing_1}px`,
          paddingX: `${padding.card}px`
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ModInputField
            size="small"
            readOnly
            placeholder="Search Recipes"
            inputProps={{ 'aria-label': 'Search recipes' }}
            startIcon={<SearchIcon size={iconSize.md} />}
            onClick={handleSearchClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSearchClick();
              }
            }}
            sx={{ cursor: 'pointer', '& input': { cursor: 'pointer' } }}
          />
        </Box>
        <ModIconButton
          size="small"
          aria-label="Close navigation menu"
          onClick={onClose}
        >
          <CloseIcon size={iconSize.md} />
        </ModIconButton>
      </Stack>
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          gap: `${spacing.spacing_1}px`,
          paddingX: `${padding.card}px`,
          paddingBottom: `calc(${spacing.spacing_2}px + env(safe-area-inset-bottom))`
        }}
      >
        {/* Primary nav rows */}
        <Stack>
          {navItems.map((item) => (
            <OverflowMenuRow
              key={item.label}
              href={item.href}
              icon={item.icon}
              label={item.label}
              beta={item.beta}
              tag={item.tag}
              selected={isRouteSelected(asPath, item.href)}
              onClick={onClose}
            />
          ))}
        </Stack>
        <ModDivider sx={{ my: 0 }} />
        {/* Collapsible sections */}
        <Stack>
          <OverflowMenuSection heading="Resources">
            {RESOURCE_LINKS.map((link) => (
              <HelpLinkRow key={link.label} link={link} onClose={onClose} />
            ))}
          </OverflowMenuSection>

          <OverflowMenuSection heading="Tools">
            {TOOL_LINKS.map((link) => (
              <HelpLinkRow key={link.label} link={link} onClose={onClose} />
            ))}
            <ModerneCliRow cliLinkOverride={cliDownload} onClose={onClose} />
          </OverflowMenuSection>

          <OverflowMenuSection heading="Need more help">
            {helpLoading ? (
              <ModSkeleton width="100%" height={`${spacing.spacing_3}px`} />
            ) : moreHelp ? (
              moreHelp.map((helpItem, index) => (
                <OverflowMenuRow
                  key={index}
                  label={helpItem.label}
                  href={helpItem.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  indent
                />
              ))
            ) : (
              FALLBACK_HELP_LINKS.map((link) => (
                <HelpLinkRow key={link.label} link={link} onClose={onClose} />
              ))
            )}
          </OverflowMenuSection>
        </Stack>
        {/* Account footer */}
        <OverflowMenuAccountFooter onClose={onClose} />
      </Stack>
    </Stack>
  );
};
